// fetchDiscoverUsers.js
'use strict';
import parseLinkHeader from 'parse-link-header';

import fetch from '../resource/customFetch.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import handleFetchError from '../resource/handleFetchError.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import { getIsIos10, getIsMicrosoftEdge } from '../resource/getUserAgent.js';
import objectifyArrayById from '../resource/objectifyArrayById.js';
import { getMetadataThumbnailFields } from '../action/savePosts.js';
import getMeData from '../selector/getMeData.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import {
  ADD_LIST_ITEMS,
  ADD_USERS,
  ADD_MESSAGES,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';
import getMessageList from '../selector/getMessageList.js';

const ONE_MINUTE = 1000 * 60;

/**
 * Fetch discover users
 * @kind action
 * @param {string} [{category}='more'] - discover user catrgory.
 * @param {number} [{page}=1] - page.
 * @param {number} [{limit}=10] - limit.
 * @param {object} {[httpProxyHeaders = {}]} - http proxy headers for SSR.
 * @return {Promise} Action promise.
 * @todo Debounce same query.
 */
const fetchDiscoverUsers =
  ({ category = 'more', page = 1, limit = 10, httpProxyHeaders = {} }) =>
  async (dispatch, getState) => {
    // End of pages
    if (page === null) return;

    const state = getState();
    const selectPath = ['discover', category, page];
    const listSelectPath = ['discover', category];
    const lastFetchedTimestamp =
      getNetworkingData(state, [...selectPath], 'fetchedTimestamp') || 0;
    const shouldFetchNewData = Date.now() >= lastFetchedTimestamp + ONE_MINUTE;

    if (!shouldFetchNewData) {
      return dispatch({
        type: '',
      });
    }

    const token = getMeData(state, 'token');
    const fetchOptions = {
      headers: {
        ...getHeaders(),
        ...httpProxyHeaders,
      },
    };

    if (token) {
      fetchOptions.headers.Authorization = `Bearer ${token}`;
    }

    const url = getResourceUrl({ endpoint: `/feeds/${category}` });
    url.searchParams.append('page', page);
    url.searchParams.append('limit', limit);

    if (getIsIos10() || getIsMicrosoftEdge()) {
      url.searchParams.append('no-redirect', 1);
    }

    dispatch({ type: SET_NETWORKING_FETCHING, payload: { selectPath } });

    try {
      let response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl: url,
        });
      }
      const totalCount = parseInt(response.headers.get('x-total-count'), 10);
      const links = parseLinkHeader(response.headers.get('Link'));
      let nextPage = null;
      let lastPage = null;
      if (links && links.next) {
        nextPage = +links.next.page;
      }
      if (links && links.last) {
        lastPage = +links.last.page;
      }

      const payload = await response.json();
      const formatedPayload = payload.map(user => {
        const { metadata, ...rest } = user;
        const userObject = { ...rest };
        if (metadata && metadata.latestMessage) {
          userObject.latestMessageId = metadata.latestMessage.id;
        }
        delete user.isFollowing;
        return userObject;
      });
      const users = objectifyArrayById({ array: formatedPayload });
      const itemIds = Object.keys(users);
      const messageItemIds = formatedPayload
        .map(item => item.latestMessageId)
        .filter(messageId => !!messageId);
      const messages = objectifyArrayById({
        array: payload
          .filter(user => user.metadata && user.metadata.latestMessage)
          .map(user => {
            return {
              ...user.metadata.latestMessage,
              senderId: user.id,
              ...getMetadataThumbnailFields({
                message: user.metadata.latestMessage,
              }),
            };
          }),
      });

      const originMessageTotalCount =
        getMessageList(getState(), 'discover', listSelectPath, 'totalCount') ||
        0;
      await Promise.all([
        dispatch({ type: ADD_USERS, payload: { users } }),
        dispatch({
          type: ADD_LIST_ITEMS,
          payload: {
            selectPath: listSelectPath,
            itemIds,
            nextPage,
            lastPage,
            totalCount,
          },
        }),
        dispatch({ type: ADD_MESSAGES, payload: { messages } }),
        dispatch({
          type: ADD_LIST_ITEMS,
          payload: {
            selectPath: ['discover', category, 'messages'],
            itemIds: messageItemIds,
            nextPage: null,
            lastPage: null,
            totalCount: originMessageTotalCount + messageItemIds?.length,
          },
        }),
      ]);
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath, error },
      });
    }
  };

export default fetchDiscoverUsers;
